import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Navigate } from 'react-router-dom';
import { revenueAPI, leadsAPI, studentsAPI } from '../services/api';
import { useRole } from '../hooks/useRole';

const EVENT_TYPES = [
  { value: 'enrollment',       label: 'Enrollment',       color: '#6366f1' },
  { value: 'mou_signed',       label: 'MoU Signed',       color: '#22c55e' },
  { value: 'franchise_signed', label: 'Franchise Signed', color: '#f59e0b' },
  { value: 'corporate_deal',   label: 'Corporate Deal',   color: '#0ea5e9' },
];

const PAY_STATUS = {
  collected: { label: 'Collected', color: '#22c55e' },
  partial:   { label: 'Partial',   color: '#f59e0b' },
  pending:   { label: 'Pending',   color: '#ef4444' },
};

const fmt = n => n >= 1_00_000
  ? `₹${(n / 1_00_000).toFixed(1)}L`
  : n >= 1000 ? `₹${(n / 1000).toFixed(0)}K` : `₹${n || 0}`;

function Modal({ show, onClose, children }) {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 640, width: '95%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>✕</button>
        {children}
      </div>
    </div>
  );
}

function RevenuePageContent() {
  const [dash, setDash] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({
    event_type: 'enrollment', deal_value: 126000, gst_amount: '', total_collected: 0,
    payment_status: 'pending', payment_date: '', closed_by: '', region: '', notes: '',
    lead_id: '', student_id: '',
  });
  const [saving, setSaving] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, eRes] = await Promise.all([
        revenueAPI.getDashboard(),
        revenueAPI.getEvents({ limit: 50 }),
      ]);
      setDash(dRes.data.data);
      setEvents(eRes.data.data || []);
    } catch {
      showToast('Failed to load revenue data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFormChange = (k, v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k === 'deal_value') next.gst_amount = Math.round(Number(v) * 0.18);
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.event_type || !form.deal_value) return showToast('Event type and deal value are required', 'error');
    setSaving(true);
    try {
      await revenueAPI.createEvent(form);
      showToast('Revenue event logged');
      setShowModal(false);
      setForm({ event_type: 'enrollment', deal_value: 126000, gst_amount: '', total_collected: 0, payment_status: 'pending', payment_date: '', closed_by: '', region: '', notes: '', lead_id: '', student_id: '' });
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>;

  const d = dash || {};
  const chartData = d.chart_data || [];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'error' ? '#fee2e2' : '#dcfce7',
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          borderRadius: 8, padding: '12px 20px', fontWeight: 600, fontSize: 14,
        }}>{toast.msg}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>Revenue Dashboard</h1>
        <button
          onClick={() => setShowModal(true)}
          style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
        >+ Log Revenue Event</button>
      </div>

      {/* Top Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Revenue This Month', value: fmt(d.total_revenue_month), sub: 'INR excl. GST' },
          { label: 'Revenue This Quarter', value: fmt(d.total_revenue_quarter), sub: 'INR excl. GST' },
          { label: 'Enrollments This Month', value: d.enrollments_month || 0, sub: 'students enrolled' },
          { label: 'MoUs + Franchise', value: d.deals_month || 0, sub: 'deals this month' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '20px 24px' }}>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1e293b' }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16, marginTop: 0 }}>Revenue by Event Type — Last 6 Months</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => `₹${v?.toLocaleString('en-IN')}`} />
            <Legend />
            <Bar dataKey="enrollment" name="Enrollments" fill="#6366f1" radius={[4, 4, 0, 0]} />
            <Bar dataKey="mou" name="MoUs" fill="#22c55e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="franchise" name="Franchise" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Bar dataKey="corporate" name="Corporate" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent Events Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16, marginTop: 0 }}>Recent Revenue Events</h3>
        {events.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No revenue events yet. Click "+ Log Revenue Event" to add one.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Date', 'Type', 'Lead / Student', 'Deal Value', 'GST', 'Closed By', 'Region', 'Payment'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => {
                  const et = EVENT_TYPES.find(t => t.value === ev.event_type);
                  const ps = PAY_STATUS[ev.payment_status] || PAY_STATUS.pending;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 12 }}>
                        {ev.createdAt ? new Date(ev.createdAt).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: `${et?.color}20`, color: et?.color }}>
                          {et?.label || ev.event_type}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#374151' }}>
                        {ev.lead_id?.contact_name || ev.student_id?.full_name || '—'}
                        {ev.lead_id?.company_name && <div style={{ color: '#94a3b8', fontSize: 11 }}>{ev.lead_id.company_name}</div>}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e293b' }}>₹{(ev.deal_value || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>₹{(ev.gst_amount || 0).toLocaleString('en-IN')}</td>
                      <td style={{ padding: '10px 12px', color: '#374151' }}>{ev.closed_by?.name || '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>{ev.region || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: `${ps.color}20`, color: ps.color }}>
                          {ps.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Log Revenue Event Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginTop: 0, marginBottom: 20 }}>Log Revenue Event</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Event Type *</label>
            <select value={form.event_type} onChange={e => handleFormChange('event_type', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
              {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Deal Value (INR) *</label>
            <input type="number" value={form.deal_value} onChange={e => handleFormChange('deal_value', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>GST (auto at 18%)</label>
            <input type="number" value={form.gst_amount || Math.round((form.deal_value || 0) * 0.18)} onChange={e => handleFormChange('gst_amount', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Total Collected</label>
            <input type="number" value={form.total_collected} onChange={e => handleFormChange('total_collected', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Payment Status</label>
            <select value={form.payment_status} onChange={e => handleFormChange('payment_status', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
              <option value="collected">Collected</option>
              <option value="partial">Partial</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Payment Date</label>
            <input type="date" value={form.payment_date} onChange={e => handleFormChange('payment_date', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Region</label>
            <input value={form.region} onChange={e => handleFormChange('region', e.target.value)} placeholder="e.g. Maharashtra"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Notes</label>
            <textarea value={form.notes} onChange={e => handleFormChange('notes', e.target.value)} rows={3}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button onClick={() => setShowModal(false)} style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            {saving ? 'Saving...' : 'Log Event'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default function RevenuePage() {
  const { isIntern, isSupport } = useRole();
  if (isIntern || isSupport) return <Navigate to="/dashboard" replace />;
  return <RevenuePageContent />;
}
